import { normalizePersistedSnapshotUrl } from '../../../../../../helpers/layoutSnapshotCapture';
import { useViewerStore } from '../../../../../../store/store';
import type { AiDesignsDataSource } from '../sections/DashboardAiDesignsSourceBadge';
import type { CategoryLayoutCandidate } from './categoryBestVariantScoring';
import { getCategoryMainLayoutKey } from './categoryBestVariantScoring';

export const SNAPSHOT_NOT_SAVED_IN_DB_MESSAGE = 'Snapshot was not saved in DB';
export const TREE_CUT_SNAPSHOT_UNAVAILABLE_MESSAGE =
    'Snapshot was not saved in DB';

export type TreeCutScenarioSnapshotRow = {
    snapshotUrl?: string;
    layoutSnapshotUrl?: string;
    snapshotCaptureError?: string;
    actualWattage?: number;
};

type CategoryVariant = {
    id?: string;
    snapshotUrl?: string;
    snapshotCaptureError?: string;
    productionW?: number;
};

export type CatalogCardSnapshotResult = {
    url?: string;
    error?: string;
};

/** While layout thumbnails are still loading from DB/cache, show the loader — not a missing-snapshot error. */
export function catalogCardSnapshotForDisplay(
    result: CatalogCardSnapshotResult,
    snapshotsLoading: boolean,
): CatalogCardSnapshotResult {
    if (snapshotsLoading && !result.url) {
        return {};
    }
    return result;
}

function nonEmptySnapshot(url: string | undefined | null): string | undefined {
    return normalizePersistedSnapshotUrl(url);
}

function firstVariantSnapshot(variants: CategoryVariant[]): string | undefined {
    for (const v of variants) {
        const snap = nonEmptySnapshot(v?.snapshotUrl);
        if (snap) return snap;
    }
    return undefined;
}

function firstVariantSnapshotError(variants: CategoryVariant[]): string | undefined {
    for (const v of variants) {
        const err = typeof v?.snapshotCaptureError === 'string' ? v.snapshotCaptureError.trim() : '';
        if (err) return err;
    }
    return undefined;
}

function missingSnapshotError(
    aiDesignsDataSource: AiDesignsDataSource | null | undefined,
    fallback = 'Layout snapshot was not captured for this category',
): string {
    if (aiDesignsDataSource === 'db') return SNAPSHOT_NOT_SAVED_IN_DB_MESSAGE;
    return fallback;
}

/**
 * Default-maxfill thumbnail fallback. When catalogue data came from the DB, only use an
 * explicitly saved `default` snapshot — never the live `defaultInitial` viewer capture.
 */
export function resolveDefaultFallbackSnapshotUrl(
    layoutSnapshotsByKey: Record<string, string | undefined> = {},
    aiDesignsDataSource?: AiDesignsDataSource | null,
): string | undefined {
    const savedDefault = nonEmptySnapshot(layoutSnapshotsByKey.default);
    if (savedDefault) return savedDefault;
    if (aiDesignsDataSource === 'db') return undefined;
    return nonEmptySnapshot((layoutSnapshotsByKey as { defaultInitial?: string }).defaultInitial);
}

export type AltLayoutVariant = {
    label?: string;
    roofIds?: string[];
    dir?: unknown;
    snapshotUrl?: string;
    productionW?: number;
    modules?: number;
    snapshotCaptureError?: string;
};

/**
 * Resolve thumbnail for an alternate / category layout variant row.
 * When catalogue data is from the DB, only use snapshots that were explicitly saved
 * for that variant — never unrelated layout or live viewer captures.
 */
export function resolveAltLayoutVariantSnapshot(params: {
    variant: AltLayoutVariant;
    aiLayoutVariantSnapshots?: Record<string, string>;
    layoutSnapshotsByKey?: Record<string, string | undefined>;
    selectedRoofCombinationKey?: string;
    aiDesignsDataSource?: AiDesignsDataSource | null;
}): CatalogCardSnapshotResult {
    const {
        variant: v,
        aiLayoutVariantSnapshots = {},
        layoutSnapshotsByKey = {},
        selectedRoofCombinationKey,
        aiDesignsDataSource = null,
    } = params;

    const direct = nonEmptySnapshot(v.snapshotUrl);
    if (direct) return { url: direct };

    const rawLabel = String(v.label ?? '').trim();
    const roofIds = Array.isArray(v.roofIds) ? v.roofIds : [];
    const dir = v.dir ?? null;
    const snapMap = aiLayoutVariantSnapshots || {};

    const keyFor = (label: string, ids: string[]) =>
        JSON.stringify({
            label,
            roofIds: ids,
            dir,
        });

    const tryHit = (label: string, ids: string[]) => nonEmptySnapshot(snapMap[keyFor(label, ids)]);

    const exact = tryHit(rawLabel, roofIds);
    if (exact) return { url: exact };

    const sorted = tryHit(rawLabel, [...roofIds].sort());
    if (sorted) return { url: sorted };

    const sortedTarget = [...roofIds].sort().join('\u0000');
    for (const [jsonKey, url] of Object.entries(snapMap)) {
        const hit = nonEmptySnapshot(url);
        if (!hit) continue;
        try {
            const p = JSON.parse(jsonKey) as { roofIds?: string[]; dir?: unknown; label?: string };
            if (String(p.label ?? '').trim() !== rawLabel) continue;
            if ((p.dir ?? null) !== dir) continue;
            if ([...(p.roofIds || [])].sort().join('\u0000') !== sortedTarget) continue;
            return { url: hit };
        } catch {
            continue;
        }
    }

    const hasMetrics =
        (typeof v.productionW === 'number' && Number.isFinite(v.productionW) && v.productionW > 0) ||
        (typeof v.modules === 'number' && Number.isFinite(v.modules) && v.modules > 0);

    if (aiDesignsDataSource === 'db') {
        if (hasMetrics) return { error: SNAPSHOT_NOT_SAVED_IN_DB_MESSAGE };
        return {};
    }

    if (selectedRoofCombinationKey) {
        const selected = nonEmptySnapshot(layoutSnapshotsByKey[selectedRoofCombinationKey]);
        if (selected) return { url: selected };
    }
    const fallback = resolveDefaultFallbackSnapshotUrl(layoutSnapshotsByKey, aiDesignsDataSource);
    if (fallback) return { url: fallback };
    for (const u of Object.values(layoutSnapshotsByKey)) {
        const hit = nonEmptySnapshot(u);
        if (hit) return { url: hit };
    }

    if (hasMetrics) {
        const variantErr =
            typeof v.snapshotCaptureError === 'string' ? v.snapshotCaptureError.trim() : '';
        if (variantErr) return { error: variantErr };
        return { error: missingSnapshotError(aiDesignsDataSource) };
    }

    return {};
}

export function resolveAltLayoutVariantSnapshotUrl(params: {
    variant: AltLayoutVariant;
    aiLayoutVariantSnapshots?: Record<string, string>;
    layoutSnapshotsByKey?: Record<string, string | undefined>;
    selectedRoofCombinationKey?: string;
    aiDesignsDataSource?: AiDesignsDataSource | null;
}): string | undefined {
    return resolveAltLayoutVariantSnapshot(params).url;
}

/**
 * Resolve the thumbnail for a dashboard AI catalog card.
 * Prefer the current run's layout snapshot map and the winning category candidate —
 * never a random first variant or unrelated fallback for category cards.
 */
function snapshotFromLayoutKeys(
    layoutSnapshotsByKey: Record<string, string | undefined>,
    ...keys: Array<string | undefined>
): string | undefined {
    for (const key of keys) {
        const hit = nonEmptySnapshot(key ? layoutSnapshotsByKey[key] : undefined);
        if (hit) return hit;
    }
    return undefined;
}

export function resolveCatalogCardSnapshot(params: {
    layoutKey: string;
    categoryLabel?: string;
    layoutSnapshotsByKey?: Record<string, string | undefined>;
    categoryMainLayoutByCategory?: Record<string, CategoryLayoutCandidate>;
    aiCategoryLayoutVariants?: Record<string, unknown[]>;
    aiLayoutVariantSnapshots?: Record<string, string>;
    /** Only used for the `default` layout when no direct snapshot exists. */
    defaultFallbackSnapshotUrl?: string;
    aiDesignsDataSource?: AiDesignsDataSource | null;
    /** When true, missing thumbnails surface an error instead of an empty placeholder. */
    layoutHasMetrics?: boolean;
}): CatalogCardSnapshotResult {
    const {
        layoutKey,
        categoryLabel,
        layoutSnapshotsByKey = {},
        categoryMainLayoutByCategory = {},
        aiCategoryLayoutVariants = {},
        aiLayoutVariantSnapshots = {},
        defaultFallbackSnapshotUrl,
        aiDesignsDataSource = null,
        layoutHasMetrics = false,
    } = params;

    const catKey = categoryLabel ? getCategoryMainLayoutKey(categoryLabel) : undefined;
    const direct = snapshotFromLayoutKeys(layoutSnapshotsByKey, layoutKey, catKey, categoryLabel);
    if (direct) return { url: direct };

    if (categoryLabel) {
        const main = categoryMainLayoutByCategory[categoryLabel];
        const mainSnap = nonEmptySnapshot(main?.snapshotUrl);
        if (mainSnap) return { url: mainSnap };

        const variants = (aiCategoryLayoutVariants[categoryLabel] ?? []) as CategoryVariant[];
        const variantSnapMap = aiLayoutVariantSnapshots || {};

        const resolveVariantRowSnapshot = (variant: CategoryVariant & AltLayoutVariant): string | undefined => {
            const embedded = nonEmptySnapshot(variant?.snapshotUrl);
            if (embedded) return embedded;
            return resolveAltLayoutVariantSnapshot({
                variant,
                aiLayoutVariantSnapshots: variantSnapMap,
                layoutSnapshotsByKey,
                aiDesignsDataSource,
            }).url;
        };

        if (main?.variantId) {
            const matched = variants.find((v) => v?.id === main.variantId);
            const matchedSnap = matched ? resolveVariantRowSnapshot(matched) : undefined;
            if (matchedSnap) return { url: matchedSnap };
        }

        if (main && typeof main.production === 'number' && Number.isFinite(main.production)) {
            const targetProd = Math.round(main.production);
            const prodMatch = variants.find(
                (v) =>
                    typeof v.productionW === 'number' &&
                    Number.isFinite(v.productionW) &&
                    Math.round(v.productionW) === targetProd,
            );
            const prodSnap = prodMatch ? resolveVariantRowSnapshot(prodMatch) : undefined;
            if (prodSnap) return { url: prodSnap };
        }

        if (main?.label || main?.roofIds?.length) {
            const mainVariantSnap = resolveVariantRowSnapshot({
                label: main.label,
                roofIds: main.roofIds,
                dir: main.dir,
                productionW: main.production,
                modules: main.modules,
            });
            if (mainVariantSnap) return { url: mainVariantSnap };
        }

        for (const variant of variants) {
            const anyVariantSnap = resolveVariantRowSnapshot(variant);
            if (anyVariantSnap) return { url: anyVariantSnap };
        }

        const anyVariantSnap = firstVariantSnapshot(variants);
        if (anyVariantSnap) return { url: anyVariantSnap };

        if (typeof main?.snapshotError === 'string' && main.snapshotError.trim()) {
            return { error: main.snapshotError.trim() };
        }

        const variantErr = firstVariantSnapshotError(variants);
        if (variantErr) return { error: variantErr };

        if (main && main.modules > 0) {
            return { error: missingSnapshotError(aiDesignsDataSource) };
        }
    }

    if (layoutKey === 'default') {
        const fallback = nonEmptySnapshot(defaultFallbackSnapshotUrl);
        if (fallback) return { url: fallback };
        if (layoutHasMetrics && aiDesignsDataSource === 'db') {
            for (const u of Object.values(layoutSnapshotsByKey)) {
                const hit = nonEmptySnapshot(u);
                if (hit) return { url: hit };
            }
        }
        if (layoutHasMetrics) {
            return { error: missingSnapshotError(aiDesignsDataSource) };
        }
    }

    return {};
}

/**
 * Tree-cutting scenario thumbnails must come from the scenario's own capture only —
 * never from the parent layout's `layoutSnapshotsByKey` entry.
 */
export function resolveTreeCutScenarioSnapshot(
    scenario: TreeCutScenarioSnapshotRow | null | undefined,
    aiDesignsDataSource?: AiDesignsDataSource | null,
): CatalogCardSnapshotResult {
    const direct = nonEmptySnapshot(scenario?.snapshotUrl);
    if (direct) return { url: direct };

    const scenarioLayout = nonEmptySnapshot(scenario?.layoutSnapshotUrl);
    if (scenarioLayout) return { url: scenarioLayout };

    const captureErr =
        typeof scenario?.snapshotCaptureError === 'string' ? scenario.snapshotCaptureError.trim() : '';
    if (captureErr) return { error: captureErr };

    const hasMetrics =
        typeof scenario?.actualWattage === 'number' &&
        Number.isFinite(scenario.actualWattage) &&
        scenario.actualWattage > 0;

    if (!hasMetrics) return {};

    if (aiDesignsDataSource === 'db' || aiDesignsDataSource === 'cache') {
        return { error: TREE_CUT_SNAPSHOT_UNAVAILABLE_MESSAGE };
    }

    return { error: 'Tree cut snapshot was not captured for this scenario' };
}

export function resolveCatalogCardSnapshotUrl(params: {
    layoutKey: string;
    categoryLabel?: string;
    layoutSnapshotsByKey?: Record<string, string | undefined>;
    categoryMainLayoutByCategory?: Record<string, CategoryLayoutCandidate>;
    aiCategoryLayoutVariants?: Record<string, unknown[]>;
    aiLayoutVariantSnapshots?: Record<string, string>;
    defaultFallbackSnapshotUrl?: string;
    aiDesignsDataSource?: AiDesignsDataSource | null;
    layoutHasMetrics?: boolean;
}): string | undefined {
    return resolveCatalogCardSnapshot(params).url;
}

/** Drop in-memory layout thumbnails before a fresh AI run so cards cannot reuse stale cache/DB images. */
export function clearAiLayoutSnapshotStateForRun(): void {
    useViewerStore.setState({
        layoutSnapshotsByKey: {},
        aiLayoutVariantSnapshots: {},
        aiCategoryLayoutVariants: {},
        categoryMainLayoutByCategory: {},
        aiResolvedRoofCombinations: [],
    } as any);
}
